import { observer } from 'mobx-react-lite';
import { VideoIcon, Tag, Percent, Power, ArrowRightLeft, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AsyncState } from '@/components/ui/AsyncState';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { mapCameraStatus, cameraStatusToClient } from '@/lib/clientStatus';
import type { AccountService } from '@/models/account/types';

type Props = {
  services: AccountService[];
  currency: string;
  isLoading: boolean;
  error: string | null;
  onOpenTariff: (idCamera: string, cameraName: string, currentTariffName: string) => void;
  onOpenDiscount: (
    idCamera: string,
    cameraName: string,
    currentDiscount: string,
    abPlata: string
  ) => void;
  onOpenStatus: (
    idCamera: string,
    cameraName: string,
    currentStatus: 'Активный' | 'Неактивный'
  ) => void;
  onOpenOwner: (idCamera: string, cameraName: string) => void;
  onOpenHistory: (idCamera: string, cameraName: string) => void;
};

type RowProps = {
  service: AccountService;
  currency: string;
  onOpenTariff: Props['onOpenTariff'];
  onOpenDiscount: Props['onOpenDiscount'];
  onOpenStatus: Props['onOpenStatus'];
  onOpenOwner: Props['onOpenOwner'];
  onOpenHistory: Props['onOpenHistory'];
};

// eslint-disable-next-line react-refresh/only-export-components
function ServiceRow({
  service,
  currency,
  onOpenTariff,
  onOpenDiscount,
  onOpenStatus,
  onOpenOwner,
  onOpenHistory,
}: RowProps) {
  const camStatus = mapCameraStatus(service.status);
  const isActive = camStatus === 'Активный';

  return (
    <div className='grid grid-cols-12 gap-3 px-4 py-3.5 items-center hover:bg-muted/20 transition-colors'>
      <div className='col-span-2 flex items-center gap-2'>
        <VideoIcon className='w-4 h-4 text-muted-foreground shrink-0' />
        <div className='flex flex-col min-w-0'>
          <span className='text-sm text-foreground truncate'>{service.short_name}</span>
          <span className='text-xs font-mono text-muted-foreground'>{service.id_camera}</span>
        </div>
      </div>
      <div className='col-span-2 text-sm text-muted-foreground truncate'>{service.tariff_name}</div>
      <div className='col-span-1 text-sm text-right text-muted-foreground'>
        {service.ab_plata} {currency}
      </div>
      <div className='col-span-1 text-center'>
        {service.discount !== '0' ? (
          <span className='flex items-center justify-center gap-0.5 text-green-400 text-sm'>
            <Percent className='w-3 h-3' />
            {service.discount}
          </span>
        ) : (
          <span className='text-muted-foreground text-sm'>—</span>
        )}
      </div>
      <div className='col-span-2 text-sm font-semibold text-right text-foreground'>
        {service.costs} {currency}
      </div>
      <div className='col-span-1 flex justify-center'>
        <StatusBadge status={cameraStatusToClient(service.status)} label={camStatus} size='sm' />
      </div>
      <div className='col-span-3 flex justify-end gap-1'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => onOpenTariff(service.id_camera, service.short_name, service.tariff_name)}
          title='Сменить тариф'
          className='p-1.5 h-auto w-auto rounded-lg border border-border/40 text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5'
        >
          <Tag className='w-3.5 h-3.5' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          onClick={() =>
            onOpenDiscount(
              service.id_camera,
              service.short_name,
              service.discount,
              service.ab_plata
            )
          }
          title='Изменить скидку'
          className='p-1.5 h-auto w-auto rounded-lg border border-border/40 text-muted-foreground hover:text-green-400 hover:border-green-400/40 hover:bg-green-500/5'
        >
          <Percent className='w-3.5 h-3.5' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => onOpenStatus(service.id_camera, service.short_name, camStatus)}
          title={isActive ? 'Отключить' : 'Включить'}
          className={cn(
            'p-1.5 h-auto w-auto rounded-lg border',
            isActive
              ? 'border-border/40 text-muted-foreground hover:text-red-400 hover:border-red-400/40 hover:bg-red-500/5'
              : 'border-border/40 text-muted-foreground hover:text-green-400 hover:border-green-400/40 hover:bg-green-500/5'
          )}
        >
          <Power className='w-3.5 h-3.5' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => onOpenOwner(service.id_camera, service.short_name)}
          title='Передать камеру'
          className='p-1.5 h-auto w-auto rounded-lg border border-border/40 text-muted-foreground hover:text-foreground hover:border-border hover:bg-muted/50'
        >
          <ArrowRightLeft className='w-3.5 h-3.5' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => onOpenHistory(service.id_camera, service.short_name)}
          title='История изменений'
          className='p-1.5 h-auto w-auto rounded-lg border border-border/40 text-muted-foreground hover:text-blue-400 hover:border-blue-400/40 hover:bg-blue-500/5'
        >
          <Clock className='w-3.5 h-3.5' />
        </Button>
      </div>
    </div>
  );
}

export const ServicesTab = observer(function ServicesTab({
  services,
  currency,
  isLoading,
  error,
  onOpenTariff,
  onOpenDiscount,
  onOpenStatus,
  onOpenOwner,
  onOpenHistory,
}: Props) {
  return (
    <div>
      <div className='mb-4'>
        <h3 className='text-sm font-medium text-foreground'>Услуги видеонаблюдения</h3>
      </div>
      <AsyncState
        isLoading={isLoading}
        error={error}
        isEmpty={services.length === 0}
        emptyMessage='Услуги не найдены'
      >
        <div className='border border-border/30 rounded-xl overflow-hidden'>
          <div className='grid grid-cols-12 gap-3 px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/20 border-b border-border/20'>
            <div className='col-span-2'>Камера</div>
            <div className='col-span-2'>Тариф</div>
            <div className='col-span-1 text-right'>Цена</div>
            <div className='col-span-1 text-center'>Скидка</div>
            <div className='col-span-2 text-right'>Итого</div>
            <div className='col-span-1 text-center'>Статус</div>
            <div className='col-span-3' />
          </div>
          <div className='divide-y divide-border/20'>
            {services.map((service) => (
              <ServiceRow
                currency={currency}
                key={service.id_camera}
                service={service}
                onOpenTariff={onOpenTariff}
                onOpenDiscount={onOpenDiscount}
                onOpenStatus={onOpenStatus}
                onOpenOwner={onOpenOwner}
                onOpenHistory={onOpenHistory}
              />
            ))}
          </div>
        </div>
      </AsyncState>
    </div>
  );
});
